// usePrefetchOrderExplanations.ts
// Hook to prefetch order explanations for visible rows after initial page load

import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOrderExplanation } from 'models/orderExplainer/sagaActions';
import { getOrderExplanation, getOrderExplanationLoading } from 'models/orderExplainer/selector';
import { getState } from 'reducers';
import type { GridApi } from 'ag-grid-community';

interface UsePrefetchOrderExplanationsProps {
	gridApi: GridApi | null | undefined;
	enabled?: boolean;
	delay?: number; // Delay in ms before prefetching (default: 1000ms)
}

/**
 * Prefetches order explanations for visible rows after initial page load
 * This improves UX by loading tooltip data before user hovers
 */
export const usePrefetchOrderExplanations = ({
	gridApi,
	enabled = true,
	delay = 1000,
}: UsePrefetchOrderExplanationsProps) => {
	const dispatch = useDispatch();
	const hasPrefetchedRef = useRef(false);
	const prefetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (!enabled || !gridApi || hasPrefetchedRef.current) {
			return;
		}

		// Clear any existing timer
		if (prefetchTimerRef.current) {
			clearTimeout(prefetchTimerRef.current);
		}

		// Wait for delay before prefetching to avoid blocking initial render
		prefetchTimerRef.current = setTimeout(() => {
			try {
				// Get all displayed rows (visible + buffer)
				const displayedRows: any[] = [];
				gridApi.forEachNodeAfterFilterAndSort((node) => {
					if (node.data && node.data.OrderDetailID) {
						displayedRows.push(node.data);
					}
				});

				// Get unique orderDetailIds from visible rows
				const orderDetailIds = new Set<number | string>();
				displayedRows.forEach((row) => {
					if (row.OrderDetailID) {
						orderDetailIds.add(row.OrderDetailID);
					}
				});

				// Prefetch explanations for visible rows (only if not already cached/loading)
				const state = getState();
				orderDetailIds.forEach((orderDetailId) => {
					const cachedData = getOrderExplanation(state, orderDetailId);
					const isLoading = getOrderExplanationLoading(state, orderDetailId);
					
					// Only prefetch if not cached and not already loading
					if (!cachedData && !isLoading) {
						dispatch(fetchOrderExplanation({ orderDetailId }));
					}
				});

				hasPrefetchedRef.current = true;
			} catch (error) {
				console.warn('Failed to prefetch order explanations:', error);
			}
		}, delay);

		return () => {
			if (prefetchTimerRef.current) {
				clearTimeout(prefetchTimerRef.current);
			}
		};
	}, [gridApi, enabled, delay, dispatch]);

	// Reset prefetch flag when grid data changes significantly
	useEffect(() => {
		if (gridApi) {
			const handleModelUpdated = () => {
				// Reset prefetch flag on significant data changes
				// This allows re-prefetching when new data is loaded
				hasPrefetchedRef.current = false;
			};

			gridApi.addEventListener('modelUpdated', handleModelUpdated);
			return () => {
				gridApi.removeEventListener('modelUpdated', handleModelUpdated);
			};
		}
	}, [gridApi]);
};

